import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  EventEmitter,
  input,
  InputSignal,
  Output,
  signal,
  Signal,
  WritableSignal
} from '@angular/core';
import { capitalizeFirstLetter, formatDateToString, getActiveDateLocale } from '@core/utils';
import { DateFormats } from '@core/enums';
import {
  addDays,
  addMonths,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  isToday,
  parseISO,
  startOfMonth,
  startOfWeek
} from 'date-fns';
import { SzIconComponent } from '../../display/sz-icon';

interface SzCalendarDay {
  readonly iso: string;
  readonly day: number;
  readonly inMonth: boolean;
  readonly isToday: boolean;
  readonly isSelected: boolean;
  readonly disabled: boolean;
  readonly ariaLabel: string;
}

const DAYS_IN_GRID: number = 42;

@Component({
  selector: 'sz-calendar',
  templateUrl: './sz-calendar.component.html',
  styleUrls: ['./sz-calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [SzIconComponent]
})
export class SzCalendarComponent {
  readonly value: InputSignal<string | null> = input<string | null>(null);
  readonly min: InputSignal<string | null> = input<string | null>(null);
  readonly max: InputSignal<string | null> = input<string | null>(null);

  @Output() readonly valueChange: EventEmitter<string> = new EventEmitter<string>();

  private readonly viewMonth: WritableSignal<Date> = signal<Date>(startOfMonth(new Date()));

  constructor() {
    // Gdy data przyjdzie z zewnątrz, kalendarz przeskakuje na jej miesiąc.
    effect((): void => {
      const iso: string | null = this.value();
      if (iso !== null && iso.length > 0) {
        this.viewMonth.set(startOfMonth(parseISO(iso)));
      }
    });
  }

  readonly monthTitle: Signal<string> = computed((): string =>
    capitalizeFirstLetter(formatDateToString(this.viewMonth(), DateFormats.MonthYear))
  );

  readonly weekdayLabels: Signal<ReadonlyArray<string>> = computed((): ReadonlyArray<string> => {
    const start: Date = startOfWeek(new Date(), { locale: getActiveDateLocale() });
    return Array.from({ length: 7 }, (_: unknown, i: number): string =>
      capitalizeFirstLetter(formatDateToString(addDays(start, i), DateFormats.WeekdayShort))
    );
  });

  readonly weeks: Signal<ReadonlyArray<ReadonlyArray<SzCalendarDay>>> = computed(
    (): ReadonlyArray<ReadonlyArray<SzCalendarDay>> => {
      const month: Date = this.viewMonth();
      const selectedIso: string | null = this.value();
      const minDate: Date | null = this.toDate(this.min());
      const maxDate: Date | null = this.toDate(this.max());
      const gridStart: Date = startOfWeek(startOfMonth(month), { locale: getActiveDateLocale() });

      const days: SzCalendarDay[] = Array.from({ length: DAYS_IN_GRID }, (_: unknown, i: number): SzCalendarDay => {
        const date: Date = addDays(gridStart, i);
        const iso: string = formatDateToString(date, DateFormats.IsoDate);
        const disabled: boolean =
          (minDate !== null && isBefore(date, minDate)) || (maxDate !== null && isAfter(date, maxDate));
        return {
          iso,
          day: date.getDate(),
          inMonth: isSameMonth(date, month),
          isToday: isToday(date),
          isSelected: selectedIso !== null && isSameDay(date, parseISO(selectedIso)),
          disabled,
          ariaLabel: capitalizeFirstLetter(formatDateToString(date, DateFormats.WeekdayFullDate))
        };
      });

      return Array.from(
        { length: 6 },
        (_: unknown, w: number): ReadonlyArray<SzCalendarDay> => days.slice(w * 7, w * 7 + 7)
      );
    }
  );

  prevMonth(): void {
    this.viewMonth.update((m: Date): Date => addMonths(m, -1));
  }

  nextMonth(): void {
    this.viewMonth.update((m: Date): Date => addMonths(m, 1));
  }

  select(day: SzCalendarDay): void {
    if (day.disabled) {
      return;
    }
    this.valueChange.emit(day.iso);
  }

  private toDate(iso: string | null): Date | null {
    return iso !== null && iso.length > 0 ? parseISO(iso) : null;
  }
}
